// src/pages/WHSArchive.tsx — G-09: WHS Document Archive & Retention Schedule
// Enforces WHS-specific retention periods per Queensland legislation.
// WHS Reg 2011 s.36 (incidents: 5yr) · s.330 (health monitoring: 30yr)
// Integrates with the existing ArchiveCentre; provides WHS-specific retention view.

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_WHS_RETENTION, WHS_RETENTION_SCHEDULE, WHSRetentionEntry } from '../utils/mockDataWHSMgmt'
import { MOCK_JOBS } from '../utils/mockData'
import { Archive, AlertTriangle, CheckCircle, Clock, Download, Shield, Search, Lock, Trash2, X, ChevronRight } from 'lucide-react'

const fmtDate = (s: string | null) => s ? new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

const RETENTION_STATUS_META: Record<WHSRetentionEntry['status'], { label: string; bg: string; text: string; dot: string }> = {
  RETAINED:                 { label: 'Retained ✓',              bg: 'bg-green-50',  text: 'text-green-700',  dot: 'bg-green-500' },
  DUE_FOR_REVIEW:           { label: 'Due for review',          bg: 'bg-amber-50',  text: 'text-amber-700',  dot: 'bg-amber-500' },
  PERMANENT_HOLD:           { label: 'Permanent hold — do not destroy', bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
  APPROVED_FOR_DESTRUCTION: { label: 'Approved for destruction', bg: 'bg-gray-100',  text: 'text-gray-500',   dot: 'bg-gray-400' },
}

export function WHSArchive() {
  const { isTier } = useAuthStore()
  const canApprove = isTier('B_PM') || isTier('A_DEVELOPER')
  const [records, setRecords] = useState<WHSRetentionEntry[]>(MOCK_WHS_RETENTION)
  const [filter, setFilter]   = useState<WHSRetentionEntry['status'] | 'ALL'>('ALL')
  const [jobFilter, setJobFilter] = useState('ALL')
  const [search, setSearch]   = useState('')
  const [selected, setSelected] = useState<WHSRetentionEntry | null>(null)

  const filtered = records.filter(r =>
    (filter === 'ALL' || r.status === filter) &&
    (jobFilter === 'ALL' || r.jobId === jobFilter) &&
    (!search || r.documentType.toLowerCase().includes(search.toLowerCase()) || r.documentRef.toLowerCase().includes(search.toLowerCase()))
  )

  const permanentHold   = records.filter(r => r.status === 'PERMANENT_HOLD').length
  const dueForReview    = records.filter(r => r.status === 'DUE_FOR_REVIEW').length
  const unclassified    = records.filter(r => !r.status).length

  const activeJobs = MOCK_JOBS.filter(j => !['COMPLETED', 'CLOSED'].includes(j.status))

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Archive className="w-6 h-6 text-[#2E75B6]"/>WHS Document Archive & Retention</h1>
          <p className="text-sm text-gray-500 mt-0.5">Retention schedules per WHS Reg 2011 · 5yr incidents · 30yr health monitoring · project+2yr SWMS</p>
        </div>
        <button className="btn-secondary text-sm flex items-center gap-2"><Download className="w-4 h-4"/>Export archive package</button>
      </div>

      {dueForReview > 0 && <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-amber-800"><Clock className="w-4 h-4 text-amber-600 flex-shrink-0"/><strong>{dueForReview} record{dueForReview !== 1 ? 's' : ''} due for retention review.</strong> Review whether records can be destroyed or must be held. Requires WHSO + PM dual sign-off for destruction approval.</div>}

      {/* Retention schedule reference */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b bg-gray-50">
          <div className="font-semibold text-gray-900 text-sm">WHS Retention Schedule — Queensland legislative requirements</div>
          <div className="text-xs text-gray-500 mt-0.5">Apply automatically to all WHS records generated in the platform</div>
        </div>
        <div className="grid grid-cols-2 divide-y divide-gray-100">
          {Object.entries(WHS_RETENTION_SCHEDULE).map(([type, info]) => (
            <div key={type} className={`px-5 py-3 flex items-start justify-between gap-3 ${info.permanent ? 'bg-purple-50' : ''}`}>
              <div>
                <div className="text-sm font-medium text-gray-900 flex items-center gap-1">
                  {info.permanent && <Lock className="w-3 h-3 text-purple-600"/>}
                  {type}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{info.period}</div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${info.permanent ? 'bg-purple-100 text-purple-700' : 'bg-blue-50 text-blue-600'}`}>
                {info.permanent ? 'PERMANENT' : `${info.years}yr`}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Summary metrics */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label:'Total WHS records', value:records.length, color:'text-gray-900', bg:'bg-white border-gray-200' },
          { label:'Retained', value:records.filter(r=>r.status==='RETAINED').length, color:'text-green-700', bg:'bg-green-50 border-green-200' },
          { label:'Permanent hold', value:permanentHold, color:'text-purple-700', bg:'bg-purple-50 border-purple-200' },
          { label:'Due for review', value:dueForReview, color:dueForReview>0?'text-amber-700':'text-gray-400', bg:dueForReview>0?'bg-amber-50 border-amber-200':'bg-white border-gray-200' },
        ].map(({ label, value, color, bg }) => (
          <div key={label} className={`border rounded-xl px-4 py-3 ${bg}`}>
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
            <div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Records table */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-40"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"/><input className="input pl-9 text-sm" placeholder="Search records…" value={search} onChange={e => setSearch(e.target.value)}/></div>
        <select className="input text-sm w-auto" value={jobFilter} onChange={e => setJobFilter(e.target.value)}><option value="ALL">All jobs</option>{activeJobs.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}</select>
        {(['ALL','RETAINED','PERMANENT_HOLD','DUE_FOR_REVIEW','APPROVED_FOR_DESTRUCTION'] as const).map(f => {
          const count = f === 'ALL' ? records.length : records.filter(r => r.status === f).length
          if (count === 0 && f !== 'ALL') return null
          return <button key={f} onClick={() => setFilter(f)} className={`text-xs px-3 py-1.5 rounded-full font-medium ${filter === f ? 'bg-[#1F3864] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{f === 'ALL' ? 'All' : RETENTION_STATUS_META[f].label} <span className="opacity-60">{count}</span></button>
        })}
      </div>

      <div className="flex gap-5 items-start">
        <div className={`${selected ? 'w-1/2' : 'w-full'} space-y-2 transition-all`}>
          {filtered.map(r => {
            const sm = RETENTION_STATUS_META[r.status]
            const isSel = selected?.id === r.id
            return (
              <div key={r.id} onClick={() => setSelected(isSel ? null : r)}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${isSel ? 'border-blue-400 ring-2 ring-blue-100' : r.status === 'PERMANENT_HOLD' ? 'border-purple-200 bg-purple-50/20' : r.status === 'DUE_FOR_REVIEW' ? 'border-amber-300' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${sm.dot}`}/>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium border ${sm.bg} ${sm.text}`}>{sm.label}</span>
                      <span className="text-xs font-mono text-gray-400">{r.documentRef}</span>
                      <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{r.jobNumber}</span>
                    </div>
                    <div className="font-medium text-gray-900 text-sm">{r.documentType}</div>
                    <div className="flex gap-4 text-xs text-gray-400 mt-0.5">
                      <span>Created: {fmtDate(r.createdAt)}</span>
                      <span>Retention: {r.retentionPeriodLabel}</span>
                      {r.destroyAfter && <span>Destroy after: {fmtDate(r.destroyAfter)}</span>}
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isSel ? 'text-blue-500' : 'text-gray-400'}`}/>
                </div>
              </div>
            )
          })}
          {filtered.length === 0 && <div className="bg-white border border-dashed border-gray-300 rounded-xl py-12 text-center text-gray-400 text-sm">No records match filters</div>}
        </div>

        {selected && (
          <div className="w-1/2 sticky top-6 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" style={{ maxHeight: 'calc(100vh - 200px)' }}>
            <div className="px-5 py-4 border-b bg-gray-50 flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${RETENTION_STATUS_META[selected.status].bg} ${RETENTION_STATUS_META[selected.status].text}`}>{RETENTION_STATUS_META[selected.status].label}</span>
                  {selected.status === 'PERMANENT_HOLD' && <Lock className="w-3.5 h-3.5 text-purple-600"/>}
                </div>
                <div className="font-semibold text-gray-900 text-sm">{selected.documentType}</div>
                <div className="text-xs font-mono text-gray-400">{selected.documentRef}</div>
              </div>
              <button onClick={() => setSelected(null)}><X className="w-4 h-4 text-gray-400"/></button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-xs text-gray-500 mb-0.5">Job</div><div className="font-medium">{selected.jobNumber}</div></div>
                <div><div className="text-xs text-gray-500 mb-0.5">Created</div><div className="font-medium">{fmtDate(selected.createdAt)}</div></div>
                <div><div className="text-xs text-gray-500 mb-0.5">Retention period</div><div className="font-medium">{selected.retentionPeriodLabel}</div></div>
                <div><div className="text-xs text-gray-500 mb-0.5">Destroy after</div><div className={`font-medium ${selected.destroyAfter ? 'text-gray-900' : 'text-purple-700'}`}>{selected.destroyAfter ? fmtDate(selected.destroyAfter) : 'PERMANENT'}</div></div>
              </div>

              {selected.notes && (
                <div><div className="text-xs text-gray-500 mb-1">Notes</div><div className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">{selected.notes}</div></div>
              )}

              {selected.status === 'PERMANENT_HOLD' && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3 text-xs text-purple-800">
                  <Lock className="w-3.5 h-3.5 inline mr-1.5"/>This record is subject to a <strong>permanent hold</strong> under WHS Regulation 2011 (Qld) s.330 (health monitoring records). This document <strong>cannot be destroyed</strong> under any circumstances. Contact your WHS advisor if clarification is needed.
                </div>
              )}

              {selected.status === 'DUE_FOR_REVIEW' && canApprove && (
                <div className="space-y-2">
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">This record is approaching or past its retention date. Review whether it can be destroyed or whether there is a reason to hold it further.</div>
                  <div className="flex gap-2">
                    <button className="btn-secondary text-xs flex items-center gap-1"><Clock className="w-3 h-3"/>Extend hold</button>
                    <button className="text-xs flex items-center gap-1 bg-red-50 text-red-700 border border-red-300 rounded-lg px-3 py-1.5 hover:bg-red-100"><Trash2 className="w-3 h-3"/>Approve destruction</button>
                  </div>
                  <div className="text-[10px] text-gray-400">Destruction requires WHSO + PM dual approval. Action is irreversible.</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
